/**
 * CookieScript → Google Consent Mode bridge
 *
 * PORTED FROM THE OLD THEME (shopify-site/live, assets/tracking-consent.js),
 * DELIBERATELY, ON 27 AUG 2026.
 *
 * WHAT CHANGED AND WHY
 *
 * The Horizon version of this file called
 * Shopify.customerPrivacy.setTrackingConsent(). The old theme NEVER did —
 * verified by reading the old theme's file directly out of Shopify, and
 * confirmed live: after "Accept All" on the old theme,
 * currentVisitorConsent() still reports every category as "no" and no
 * _tracking_consent cookie exists at all. Shopify's own analytics ran
 * regardless, and session-to-order attribution held at ~91% of orders.
 *
 * Writing that consent state is what changed at launch. From 18 Aug the
 * share of orders Shopify could tie back to a session fell to ~58% and
 * stayed there, which is most of the apparent conversion-rate cliff in
 * Shopify's reports (reported CVR -45%, real orders/sessions -13%).
 *
 * So: this file no longer touches the Customer Privacy API. It does exactly
 * what the old theme did and nothing more:
 *
 *   1. Google Consent Mode defaults (all denied, wait_for_update 500) plus
 *      url_passthrough, applied when CookieScript loads.
 *   2. consent_update published to Shopify.analytics on banner interaction,
 *      which the GTM custom pixel forwards to gtag. Accept and Accept All
 *      only - the old theme had no reject handler and no load-time replay.
 *   3. Internal link decoration for utm_/mtm_/gclid/fbclid/ttclid.
 *
 * Items 1 and 3 were missing from the Horizon version entirely; they are
 * restored here because "the same as before" includes them.
 *
 * If Shopify-side consent gating is ever wanted back, it belongs behind a
 * deliberate decision about the attribution cost, not as a side effect.
 */

// Manual URL passthrough
// Collect the tracking parameters present on the current URL.
function getTrackingParameters(url = window.location.href) {
  const params = ['gclid', 'fbclid', 'ttclid'];
  const regex = new RegExp('[?&]([^=#]+)=([^&#]*)', 'g');
  let match;
  let result = {};
  while ((match = regex.exec(url))) {
    const param = match[1];
    if (param.startsWith('mtm_') || param.startsWith('utm_') || params.includes(param)) {
      result[param] = match[2];
    }
  }
  return result;
}

function appendParametersToUrl(url, params) {
  let newUrl = new URL(url, window.location.origin);
  for (const key in params) {
    if (params.hasOwnProperty(key)) {
      newUrl.searchParams.set(key, params[key]);
    }
  }
  return newUrl.toString();
}

/*
 * Cached so a click costs a string compare rather than a regex sweep.
 * Keyed on location.search rather than computed once: Horizon rewrites the
 * URL through the History API for filters and pagination, so the parameters
 * on the page can change without a navigation.
 */
var cachedSearch = null;
var cachedParams = {};
var cachedHasParams = false;

function currentTrackingParameters() {
  if (window.location.search !== cachedSearch) {
    cachedSearch = window.location.search;
    cachedParams = getTrackingParameters();
    cachedHasParams = Object.keys(cachedParams).length > 0;
  }
  return cachedHasParams ? cachedParams : null;
}

function appendParametersToLinks() {
  const params = currentTrackingParameters();
  if (!params) return;
  const links = document.querySelectorAll('a[href^="/"]');
  links.forEach(function (link) {
    link.href = appendParametersToUrl(link.href, params);
  });
}

document.addEventListener('DOMContentLoaded', function () {
  appendParametersToLinks();
});

/*
 * Decorate at click time as well.
 *
 * The DOMContentLoaded pass cannot run until every deferred script has
 * executed, but links are clickable as soon as they parse. On a slow
 * connection that gap is seconds wide, and anything clicked inside it
 * navigates with a bare href - so the gclid/utm chain breaks for exactly
 * the visitors on the worst connections. This listener is live the moment
 * the script parses, which covers that window, and it also catches links
 * Horizon injects later through section morphing, which the one-shot pass
 * at load never sees.
 *
 * Capture phase, so it runs ahead of anything that stops propagation.
 * Already-decorated links hold an absolute href and no longer match
 * a[href^="/"], so they are skipped rather than rewritten twice.
 *
 * Wrapped in try/catch on purpose: link decoration must never be able to
 * break a navigation.
 */
document.addEventListener(
  'click',
  function (event) {
    try {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      const link = target.closest('a[href^="/"]');
      if (!link) return;

      const params = currentTrackingParameters();
      if (!params) return;

      link.href = appendParametersToUrl(link.href, params);
    } catch (e) {
      /* never let decoration break the click */
    }
  },
  true
);

// Consent starts denied.
var consent_data = {
  ad_storage: 'denied',
  analytics_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied'
};

function publishConsent() {
  if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') {
    return;
  }
  window.Shopify.analytics.publish('consent_update', consent_data);
}

// Partial acceptance: whichever categories the visitor ticked.
var consentPartial = function (event) {
  var detail = event && event.detail;
  if (!detail || !Array.isArray(detail.categories)) return;

  var consent_categories = new Set(detail.categories);

  if (consent_categories.has('targeting')) {
    consent_data.ad_storage = 'granted';
    consent_data.ad_user_data = 'granted';
    consent_data.ad_personalization = 'granted';
  }
  if (consent_categories.has('performance')) {
    consent_data.analytics_storage = 'granted';
  }

  publishConsent();
};

// Accept All.
var consentAll = function () {
  consent_data.ad_storage = 'granted';
  consent_data.ad_user_data = 'granted';
  consent_data.ad_personalization = 'granted';
  consent_data.analytics_storage = 'granted';

  publishConsent();
};

// Google Consent Mode defaults, applied as CookieScript comes up.
var defaultSettings = function () {
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    dataLayer.push(arguments);
  }
  gtag('consent', 'default', {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500
  });

  gtag('set', 'url_passthrough', true);
};

window.addEventListener('CookieScriptAccept', consentPartial, false);
window.addEventListener('CookieScriptAcceptAll', consentAll, false);
window.addEventListener('CookieScriptLoaded', defaultSettings, false);
